import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { User } from "@supabase/supabase-js";

interface CompletedTasks {
  [key: string]: boolean; // Format: "week-day": true/false
}

interface UseRoadmapProgressProps {
  roadmapId: string | null;
  user: User | null;
  totalDays: number;
}

export const useRoadmapProgress = ({ roadmapId, user, totalDays }: UseRoadmapProgressProps) => {
  const [completedTasks, setCompletedTasks] = useState<CompletedTasks>({});
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progressId, setProgressId] = useState<string | null>(null);

  // Load progress from database
  useEffect(() => {
    const loadProgress = async () => {
      if (!user || !roadmapId) {
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("roadmap_progress")
          .select("*")
          .eq("user_id", user.id)
          .eq("roadmap_id", roadmapId)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          const tasks = data.completed_tasks as CompletedTasks;
          setCompletedTasks(tasks);
          setProgressId(data.id);
          
          // Recalculate progress based on current totalDays instead of trusting stored value
          const completed = Object.values(tasks).filter(Boolean).length;
          const recalculatedProgress = totalDays > 0 ? Math.min(100, Math.round((completed / totalDays) * 100)) : 0;
          setProgressPercentage(recalculatedProgress);
          
          // Update database if stored progress differs from recalculated
          if (data.progress_percentage !== recalculatedProgress) {
            supabase
              .from("roadmap_progress")
              .update({ progress_percentage: recalculatedProgress })
              .eq("id", data.id)
              .then(() => console.log(`Updated progress from ${data.progress_percentage}% to ${recalculatedProgress}%`));
          }
        } else {
          // Create initial progress entry
          const { data: newProgress, error: insertError } = await supabase
            .from("roadmap_progress")
            .insert({
              user_id: user.id,
              roadmap_id: roadmapId,
              completed_tasks: {},
              progress_percentage: 0,
            })
            .select()
            .single();

          if (insertError) throw insertError;
          
          if (newProgress) {
            setProgressId(newProgress.id);
          }
        }
      } catch (error: any) {
        console.error("Error loading progress:", error);
        toast.error("Failed to load progress");
      } finally {
        setLoading(false);
      }
    };

    loadProgress();
  }, [user, roadmapId]);

  // Calculate progress percentage
  const calculateProgress = useCallback((days: CompletedTasks) => {
    if (totalDays === 0) return 0;
    const completed = Object.values(days).filter(Boolean).length;
    return Math.min(100, Math.round((completed / totalDays) * 100));
  }, [totalDays]);

  // Save progress to database (debounced)
  const saveProgress = useCallback(async (tasks: CompletedTasks) => {
    if (!user || !roadmapId || !progressId) return;

    const percentage = calculateProgress(tasks);

    try {
      const { error } = await supabase
        .from("roadmap_progress")
        .update({
          completed_tasks: tasks,
          progress_percentage: percentage,
        })
        .eq("id", progressId);

      if (error) throw error;
    } catch (error: any) {
      console.error("Error saving progress:", error);
      toast.error("Failed to save progress");
    }
  }, [user, roadmapId, progressId, calculateProgress]);

  // Toggle day completion
  const toggleDay = useCallback((dayKey: string) => {
    if (!user) {
      toast.error("Please sign in to track progress");
      return;
    }

    setCompletedTasks((prev) => {
      const newDays = { ...prev, [dayKey]: !prev[dayKey] };
      const newPercentage = calculateProgress(newDays);
      setProgressPercentage(newPercentage);
      
      // Save to database (debounced via setTimeout)
      setTimeout(() => saveProgress(newDays), 500);
      
      return newDays;
    });
  }, [user, calculateProgress, saveProgress]);

  // Check if day is completed
  const isDayCompleted = useCallback((dayKey: string) => {
    return !!completedTasks[dayKey];
  }, [completedTasks]);

  return {
    completedTasks,
    progressPercentage,
    loading,
    toggleDay,
    isDayCompleted,
  };
};
